import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { SEO } from "@/components/SEO";
import { errorMessage } from "@/lib/error-message";

const HEADLINE_FONT = "'Space Grotesk', sans-serif";
const BODY_FONT = "'Manrope', sans-serif";

/**
 * Public route hit from the Supabase password-recovery email link.
 * Supabase auto-creates a recovery session when the user lands here with
 * `type=recovery` in the URL hash — we just need to call updateUser.
 */
const ResetPassword = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [hasRecoverySession, setHasRecoverySession] = useState<boolean | null>(null);

  useEffect(() => {
    // The recovery hash is consumed by supabase-js automatically and triggers
    // an onAuthStateChange("PASSWORD_RECOVERY") event. Confirm we have a
    // session before allowing a password update.
    supabase.auth.getSession().then(({ data: { session } }) => {
      setHasRecoverySession(Boolean(session));
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || session) {
        setHasRecoverySession(Boolean(session));
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      toast({ title: "Password too short", description: "Use at least 8 characters.", variant: "destructive" });
      return;
    }
    if (password !== confirm) {
      toast({ title: "Passwords don't match", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast({ title: "Password updated", description: "You can now sign in with your new password." });
      await supabase.auth.signOut();
      navigate("/auth", { replace: true });
    } catch (err) {
      toast({ title: "Could not update password", description: errorMessage(err), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <SEO />
      <div
        className="dark flex min-h-[100dvh] flex-col items-center justify-center bg-[#0e0e11] px-6 text-[#e7e4ec]"
        style={{ fontFamily: BODY_FONT }}
      >
        <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-xl">
          <h1 className="mb-2 text-center text-xl font-bold uppercase tracking-[0.18em] text-white" style={{ fontFamily: HEADLINE_FONT }}>
            Reset password
          </h1>
          <p className="mb-5 text-center text-xs text-[#acaab1]">
            Choose a new password for your FitAura account.
          </p>

          {hasRecoverySession === false ? (
            <div className="space-y-3 text-center">
              <p className="text-sm text-red-300">This reset link is invalid or has expired.</p>
              <Button
                onClick={() => navigate("/auth", { replace: true })}
                className="w-full bg-gradient-to-r from-[#ac8aff] to-[#8f60fa] text-[#0e0e11]"
              >
                Back to sign in
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <Input
                type="password"
                placeholder="New password (min 8 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 rounded-xl border-white/10 bg-white/[0.04] text-white placeholder:text-[#76757b]"
                required
                minLength={8}
              />
              <Input
                type="password"
                placeholder="Confirm new password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="h-12 rounded-xl border-white/10 bg-white/[0.04] text-white placeholder:text-[#76757b]"
                required
                minLength={8}
              />
              <Button
                type="submit"
                disabled={busy || hasRecoverySession === null}
                className="h-12 w-full rounded-2xl bg-gradient-to-r from-[#ac8aff] to-[#8f60fa] text-xs font-bold uppercase tracking-[0.22em] text-[#0e0e11]"
                style={{ fontFamily: HEADLINE_FONT }}
              >
                {busy ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating…
                  </>
                ) : (
                  "Update password"
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
